import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	CalendarDaysIcon,
	ClockIcon,
	CurrencyDollarIcon,
	ExclamationTriangleIcon,
	UserGroupIcon,
	DocumentTextIcon,
	PaperAirplaneIcon,
	LockClosedIcon,
	EnvelopeIcon,
	PhoneIcon,
	CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Modal, Spinner, Alert } from '../../components/ui';
import SendInvoiceDialog from '../../components/SendInvoiceDialog';
import { staffProfileApi, invoiceApi, ApiError } from '../../api';
import useStaffProfileStore from '../../stores/staffProfileStore';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSION } from '../../types/permissions';
import type { StaffBookingDetail, StaffBookingBraidCalculator } from '../../types/staffBookings';

// Task badge color mapping (shared with Bookings page)
const TASK_BADGE_STYLES: Record<string, string> = {
	Liner: 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-200',
	Picker: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-200',
	Dropper: 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-200',
	Feeder: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200',
	Whole: 'bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white',
};

const getTaskBadgeStyle = (taskType: string): string => {
	return (
		TASK_BADGE_STYLES[taskType] ||
		'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
	);
};

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDateTime = (dateTimeStr: string) => {
	try {
		const date = new Date(dateTimeStr);
		const dateFormatted = date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const period = hours >= 12 ? 'PM' : 'AM';
		const displayHours = hours % 12 || 12;
		const time = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
		return { date: dateFormatted, time };
	} catch {
		return { date: '-', time: '-' };
	}
};

const formatDate = (dateTimeStr: string): string => {
	try {
		const date = new Date(dateTimeStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return '-';
	}
};

const formatMeasurement = (value: number | null): string => {
	if (value === null || !Number.isFinite(value) || value <= 0) return '—';
	return value.toFixed(2);
};

const BraidCalculatorSection = ({ calculator }: { calculator: StaffBookingBraidCalculator }) => (
	<div className="mt-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
		<div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
			<span className="text-base">📐</span>
			<h3 className="flex-1 text-sm font-bold text-gray-900 dark:text-white">
				Braid Section Estimated
			</h3>
			<div className="flex items-center gap-3">
				<div className="flex items-center gap-1.5">
					<label
						htmlFor="bcLeft"
						className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap"
					>
						Left (in)
					</label>
					<input
						id="bcLeft"
						type="number"
						readOnly
						value={calculator.leftMeasurement}
						className="w-16 px-2 py-1 text-center text-xs rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-not-allowed"
					/>
				</div>
				<div className="flex items-center gap-1.5">
					<label
						htmlFor="bcRight"
						className="text-[0.65rem] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap"
					>
						Right (in)
					</label>
					<input
						id="bcRight"
						type="number"
						readOnly
						value={calculator.rightMeasurement}
						className="w-16 px-2 py-1 text-center text-xs rounded border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-not-allowed"
					/>
				</div>
			</div>
		</div>
		<div className="px-4 py-3">
			<div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
				<table className="w-full text-xs table-fixed">
					<thead>
						<tr className="bg-gray-800 dark:bg-gray-900 text-white">
							<th
								rowSpan={2}
								className="w-[36%] px-2 py-1.5 text-left text-[0.7rem] font-semibold border-b border-white/10"
							>
								Braid Size
							</th>
							<th
								rowSpan={2}
								className="w-[22%] px-2 py-1.5 text-center text-[0.7rem] font-semibold border-b border-white/10 bg-indigo-500/40"
							>
								Total Front Rows
							</th>
							<th
								colSpan={2}
								className="w-[42%] px-2 py-1.5 text-center text-[0.7rem] font-semibold border-b border-white/15"
							>
								Section <span className="font-normal opacity-70">(in)</span>
							</th>
						</tr>
						<tr className="bg-gray-800 dark:bg-gray-900 text-white">
							<th className="px-2 py-1 text-center text-[0.65rem] font-medium bg-white/[0.08]">
								Left
							</th>
							<th className="px-2 py-1 text-center text-[0.65rem] font-medium bg-white/[0.08]">
								Right
							</th>
						</tr>
					</thead>
					<tbody>
						{calculator.rows.map((row, idx) => (
							<tr
								key={`${row.size}-${idx}`}
								className="even:bg-gray-50 dark:even:bg-gray-700/30"
							>
								<td className="px-2 py-1.5 text-left text-xs font-semibold text-gray-800 dark:text-gray-100">
									{row.size}
								</td>
								<td className="px-2 py-1.5 text-center text-xs font-bold text-[#764ba2] dark:text-indigo-300 bg-indigo-500/5 dark:bg-indigo-400/10">
									{row.totalFront ?? '—'}
								</td>
								<td className="px-2 py-1.5 text-center text-xs text-gray-600 dark:text-gray-300">
									{formatMeasurement(row.leftSection)}
								</td>
								<td className="px-2 py-1.5 text-center text-xs text-gray-600 dark:text-gray-300">
									{formatMeasurement(row.rightSection)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	</div>
);

interface BookingDetailModalProps {
	bookingId: number | null;
	onClose: () => void;
}

const BookingDetailModal = ({ bookingId, onClose }: BookingDetailModalProps) => {
	const navigate = useNavigate();
	const { profile } = useStaffProfileStore();
	const [detail, setDetail] = useState<StaffBookingDetail | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Invoicing is permission-gated per role: review (Create) and send (Send) are separate,
	// and neither grants access to the customer's contact details — the backend masks those
	// unless the role also has View Contact Details, and resolves the recipient itself.
	const { hasAny } = usePermissions();
	const canReviewInvoice = hasAny(PERMISSION.INVOICE_CREATE_OWN, PERMISSION.INVOICE_MANAGE_ALL);
	const canSendInvoice = hasAny(PERMISSION.INVOICE_SEND_OWN, PERMISSION.INVOICE_MANAGE_ALL);
	const [invoiceOpen, setInvoiceOpen] = useState(false);
	const [invoiceDefaults, setInvoiceDefaults] = useState({
		discount: 0,
		miscCharges: 0,
		miscDescription: '',
	});

	/** Silent refetch (no spinner) so the invoice state updates after a send. */
	const reloadDetail = () => {
		if (!bookingId) return;
		staffProfileApi
			.getBookingDetail(bookingId)
			.then(data => setDetail(data))
			.catch(() => {});
	};

	const openInvoice = async () => {
		if (!detail) return;
		let defaults = { discount: 0, miscCharges: 0, miscDescription: '' };
		if (detail.invoiceId) {
			try {
				const inv = await invoiceApi.getDetail(detail.invoiceId);
				defaults = {
					discount: inv.discount || 0,
					miscCharges: inv.miscCharges || 0,
					miscDescription: inv.miscDescription || '',
				};
			} catch {
				// Fall back to a blank form; the resend still targets the existing invoice.
			}
		}
		setInvoiceDefaults(defaults);
		setInvoiceOpen(true);
	};

	useEffect(() => {
		if (!bookingId) {
			// Don't clear detail here — preserves content during close animation
			setError(null);
			return;
		}

		// Clear previous booking's data before fetching new one
		setDetail(null);
		setIsLoading(true);
		setError(null);

		staffProfileApi
			.getBookingDetail(bookingId)
			.then(data => setDetail(data))
			.catch(err => {
				const apiError = err as ApiError;
				setError(apiError.message || 'Failed to load booking details');
			})
			.finally(() => setIsLoading(false));
	}, [bookingId]);

	const handleRetry = () => {
		if (bookingId) {
			setError(null);
			setIsLoading(true);
			staffProfileApi
				.getBookingDetail(bookingId)
				.then(data => setDetail(data))
				.catch(err => {
					const apiError = err as ApiError;
					setError(apiError.message || 'Failed to load booking details');
				})
				.finally(() => setIsLoading(false));
		}
	};

	const headerContent = detail ? (
		<div
			className="px-6 py-5"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="pr-10">
				<h2 className="text-xl font-bold text-white mb-1">{detail.serviceName}</h2>
				<p className="text-white/80 text-sm">
					{[detail.size, detail.hairLength].filter(Boolean).join(' \u2022 ')}
				</p>
			</div>
		</div>
	) : undefined;

	return (
		<Modal
			isOpen={bookingId !== null}
			onClose={onClose}
			size="xl"
			headerContent={headerContent}
			panelClassName="max-h-[90vh] flex flex-col"
			bodyClassName="!p-0 flex-1 flex flex-col min-h-0"
		>
			{/* Loading State */}
			{isLoading && (
				<div className="flex items-center justify-center min-h-[300px]">
					<Spinner size="lg" className="text-[#667eea]" />
				</div>
			)}

			{/* Error State */}
			{error && !isLoading && (
				<div className="px-4 py-4 sm:px-6 sm:py-5">
					<Alert
						type="warning"
						icon={<ExclamationTriangleIcon className="w-8 h-8" />}
						title="Unable to Load Details"
						message={error}
						actions={[{ label: 'Retry', onClick: handleRetry, primary: true }]}
						onDismiss={() => setError(null)}
					/>
				</div>
			)}

			{/* Content */}
			{detail && !isLoading && !error && (
				<>
					{/* Scrollable content area */}
					<div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
						{/* Quick Info Row */}
						<div className="grid grid-cols-3 gap-3 mb-5">
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<CalendarDaysIcon className="w-4 h-4 text-[#667eea]" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Date
									</span>
								</div>
								<div className="text-sm font-semibold text-gray-900 dark:text-white">
									{formatDateTime(detail.appointmentDateTime).date}
								</div>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<ClockIcon className="w-4 h-4 text-[#667eea]" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Time
									</span>
								</div>
								<div className="text-sm font-semibold text-gray-900 dark:text-white">
									{formatDateTime(detail.appointmentDateTime).time}
								</div>
							</div>
							<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-3.5">
								<div className="flex items-center gap-1.5 mb-1.5">
									<CurrencyDollarIcon className="w-4 h-4 text-emerald-500" />
									<span className="text-xs text-gray-500 dark:text-gray-400">
										Price
									</span>
								</div>
								<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
									{formatCurrency(detail.amount)}
								</div>
							</div>
						</div>

						{/* Two-column grid */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
							{/* Left Column - Client Information + Task Details */}
							<div className="space-y-5">
								{/* Client Information */}
								<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
									<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
										Client Information
									</h3>

									{/* Client avatar + name */}
									<div className="flex items-center gap-3 mb-4">
										<div
											className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
											style={{
												background:
													'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
											}}
										>
											{detail.customerInitials || '?'}
										</div>
										<div>
											<div className="text-sm font-semibold text-gray-900 dark:text-white">
												{detail.customerName}
											</div>
											<div className="text-xs text-gray-500 dark:text-gray-400">
												Client since {formatDate(detail.clientSince)} &bull;{' '}
												{detail.visitCount} visits
											</div>
										</div>
									</div>

									{/* Contact — masked by the backend unless the role has View Contact Details */}
									{!detail.customerPhone && !detail.customerEmail && (
										<div className="mb-3 text-xs text-gray-400 dark:text-gray-500 italic">
											No contact details on file
										</div>
									)}
									{(detail.customerPhone || detail.customerEmail) && (
										<div className="mb-3 space-y-1.5">
											{detail.customerPhone && (
												<div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
													<PhoneIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
													<span>{detail.customerPhone}</span>
												</div>
											)}
											{detail.customerEmail && (
												<div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
													<EnvelopeIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
													<span className="break-all">
														{detail.customerEmail}
													</span>
												</div>
											)}
											{detail.customerContactMasked && (
												<div className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
													<LockClosedIcon
														className="w-3 h-3 flex-shrink-0"
														aria-hidden="true"
													/>
													Client contact details are masked
												</div>
											)}
										</div>
									)}

									{/* Total spent */}
									{/* <div className="flex items-center justify-between py-2.5 border-t border-gray-200 dark:border-gray-600">
										<span className="text-xs text-gray-500 dark:text-gray-400">
											Total Spent
										</span>
										<span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
											{formatCurrency(detail.totalSpent)}
										</span>
									</div> */}

									{/* Notes */}
									{detail.notes && (
										<div className="mt-3 bg-white dark:bg-gray-800 border-l-3 border-[#667eea] rounded-lg p-3.5">
											<div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
												Notes
											</div>
											<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
												{detail.notes}
											</p>
										</div>
									)}
								</div>

								{/* Provider Instructions */}
								{detail.providerInstructions && (
									<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
										<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
											Provider Instructions
										</h3>
										<div className="bg-white dark:bg-gray-800 border-l-3 border-[#667eea] rounded-lg p-3.5">
											<p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
												{detail.providerInstructions}
											</p>
										</div>
									</div>
								)}
							</div>

							{/* Right Column - Service & Assignment + Team */}
							<div className="space-y-5">
								{/* Service & Assignment */}
								<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
									<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
										Service & Assignment
									</h3>

									{/* Service details grid */}
									<div className="grid grid-cols-2 gap-3 mb-4">
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Service Type
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.serviceName}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Size
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.size || '-'}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Length
											</div>
											<div className="text-sm font-medium text-gray-900 dark:text-white">
												{detail.hairLength || '-'}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
												Service Price
											</div>
											<div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
												{formatCurrency(detail.amount)}
											</div>
										</div>
									</div>

									{/* Divider */}
									<div className="border-t border-gray-200 dark:border-gray-600 my-4" />

									{/* Your Assignment */}
									<h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
										Your Tasks
									</h4>
									<div className="flex flex-wrap gap-2">
										{(() => {
											const staffId = profile?.id;
											const myTasks = staffId
												? (detail.selectedTasks ?? []).filter(t =>
														(t.staffIds ?? []).includes(staffId)
													)
												: [];
											return myTasks.length > 0 ? (
												myTasks.map(task => (
													<span
														key={task.serviceTaskId}
														className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${getTaskBadgeStyle(task.taskType)}`}
													>
														{task.taskType}
													</span>
												))
											) : (
												<span className="text-sm text-gray-400">-</span>
											);
										})()}
									</div>
								</div>

								{/* Team */}
								{detail.assignedStaff && detail.assignedStaff.length > 0 && (
									<div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 sm:p-5">
										<div className="flex items-center gap-2 mb-4">
											<UserGroupIcon className="w-4 h-4 text-[#667eea]" />
											<h3 className="text-sm font-semibold text-gray-900 dark:text-white">
												Team
											</h3>
										</div>
										<div className="flex flex-wrap gap-2">
											{detail.assignedStaff.map(staff => (
												<div
													key={staff.id}
													className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-sm"
												>
													<div
														className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
														style={{
															background:
																'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
														}}
													>
														{staff.initials}
													</div>
													<div className="text-xs font-medium text-gray-900 dark:text-white">
														{staff.name}
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>

						{/* Braid Section Estimated (only when API provides it) */}
						{detail.braidCalculator &&
							(detail.braidCalculator.rows?.length ?? 0) > 0 && (
								<BraidCalculatorSection calculator={detail.braidCalculator} />
							)}
					</div>

					{/* Sticky Footer - Invoice action (permission-gated) + Submit TCR (hidden for completed/cancelled/upcoming).
					    "started" is judged by the server on the salon clock, so the TCR button does not depend on the browser's timezone. */}
					{(() => {
						const completedForStaff =
							detail.status === 'COMPLETED' || !!detail.tcrStatus;
						const showTcr =
							detail.status !== 'CANCELLED' && !completedForStaff && detail.started;
						const showInvoice = canReviewInvoice && detail.status !== 'CANCELLED';
						// Invoices unlock after completion, unless one already exists for the booking.
						const invoiceLocked = detail.invoiceState === 'NONE' && !completedForStaff;
						if (!showTcr && !showInvoice) return null;
						const invoiceLabel = !canSendInvoice
							? 'Review Invoice'
							: detail.invoiceState === 'PENDING'
								? 'Resend Invoice'
								: 'Send Invoice';
						return (
							<div className="shrink-0 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-b-2xl flex flex-wrap items-center justify-end gap-3">
								{showInvoice &&
									(detail.invoiceState === 'PAID' ? (
										<span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
											<CheckCircleIcon className="w-4.5 h-4.5" />
											Invoice paid
										</span>
									) : invoiceLocked ? (
										<span
											className="inline-flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500"
											title="Available once the appointment is completed"
										>
											<LockClosedIcon
												className="w-4 h-4"
												aria-hidden="true"
											/>
											Invoice unlocks after completion
										</span>
									) : (
										<button
											onClick={e => {
												e.stopPropagation();
												openInvoice();
											}}
											className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-[#667eea] border-2 border-[#667eea] cursor-pointer transition-all hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
										>
											<PaperAirplaneIcon className="w-4.5 h-4.5" />
											{invoiceLabel}
										</button>
									))}
								{showTcr && (
									<button
										onClick={e => {
											e.stopPropagation();
											onClose();
											navigate(`/staff/tcr/new?appointmentId=${detail.id}`);
										}}
										className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
										style={{
											background:
												'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
										}}
									>
										<DocumentTextIcon className="w-4.5 h-4.5" />
										Submit TCR
									</button>
								)}
							</div>
						);
					})()}
				</>
			)}

			{/* Send Invoice — recipient is resolved by BBP; masked contact is display-only */}
			{detail && (
				<SendInvoiceDialog
					isOpen={invoiceOpen}
					onClose={() => setInvoiceOpen(false)}
					onSuccess={() => {
						setInvoiceOpen(false);
						reloadDetail();
					}}
					appointmentId={detail.invoiceId ? undefined : detail.id}
					invoiceId={detail.invoiceId ?? undefined}
					customerName={detail.customerName}
					serviceName={detail.serviceName}
					servicePrice={detail.amount || 0}
					deposit={detail.collectedDeposit || 0}
					sizeAndLength={[detail.size, detail.hairLength]
						.filter(Boolean)
						.join(' \u2022 ')}
					appointmentDate={formatDateTime(detail.appointmentDateTime).date}
					staffName={
						profile
							? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
							: undefined
					}
					customerEmail={detail.customerEmail ?? undefined}
					customerPhone={detail.customerPhone ?? undefined}
					defaultDiscount={invoiceDefaults.discount}
					defaultMiscCharges={invoiceDefaults.miscCharges}
					defaultMiscDescription={invoiceDefaults.miscDescription}
					canSend={canSendInvoice}
					contactMasked={detail.customerContactMasked}
				/>
			)}
		</Modal>
	);
};

export default BookingDetailModal;
